<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Link } from '@inertiajs/inertia-vue3'
import { useStore } from 'vuex'
import SearchComponent from './SearchComponent.vue'
import NotificationComponent from './NotificationComponent.vue'
import CurrencySwitcher from './CurrencySwitcher.vue'
import DarkModeSwitcher from './DarkModeSwitcher.vue'

const openMenu = ref(false)
const store = useStore()
const darkMode = computed(() => store.state.dark_mode)

const closeMenu = () => { openMenu.value = false }
const toggleMenu = () => { openMenu.value = !openMenu.value }
const handleEscape = event => {
    if (event.key === 'Escape') closeMenu()
}

watch(openMenu, isOpen => {
    if (typeof document !== 'undefined') {
        document.body.style.overflow = isOpen ? 'hidden' : ''
    }
})

onMounted(() => window.addEventListener('keydown', handleEscape))
onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleEscape)
    document.body.style.overflow = ''
})
</script>

<template>
    <nav class="sticky top-0 left-0 z-40 w-full border-b public-navigation bg-enriqueto_yellow border-enriqueto_green border-opacity-10">
        <div class="container flex items-center justify-between w-full px-4 py-3 mx-auto md:px-6 md:py-4">
            <div class="flex items-center min-w-0">
                <Link href="/" class="flex-shrink-0" aria-label="Ir al mercado de Enriqueto">
                    <img
                        class="object-contain public-navigation-logo"
                        :src="darkMode ? '/images/enriquetofrutas_darklogo.png' : '/images/enriquetofrutas_logo.png'"
                        alt="Enriqueto"
                    />
                </Link>

                <div class="items-center hidden ml-8 public-navigation-links lg:flex">
                    <Link href="/" class="public-navigation-link">Mercado</Link>
                    <Link href="/taxonomia" class="public-navigation-link">Taxonomía</Link>
                    <Link href="/portfolio" class="public-navigation-link">Portafolio</Link>
                    <Link v-if="$page.props.isAdmin === true" href="/admin" class="public-navigation-link">Administración</Link>
                    <a v-if="$page.props.isAdmin === true" href="/api/documentation" class="public-navigation-link">API</a>
                </div>
            </div>

            <div class="items-center hidden lg:flex">
                <Link
                    v-if="$page.props.user != null"
                    href="/profile"
                    class="max-w-xs mr-3 overflow-hidden text-base font-bold truncate public-navigation-action text-enriqueto_green"
                >
                    Hola, {{ $page.props.user.name }}
                </Link>
                <Link
                    v-if="$page.props.user != null"
                    href="/logout"
                    method="post"
                    as="button"
                    type="button"
                    class="px-3 py-2 text-base font-semibold rounded-lg public-navigation-action text-enriqueto_green hover:bg-white hover:bg-opacity-50"
                >
                    Cerrar sesión
                </Link>

                <a
                    v-if="$page.props.user == null"
                    href="/login"
                    class="px-4 py-2 text-base font-bold rounded-lg public-navigation-action text-enriqueto_green hover:bg-white hover:bg-opacity-50"
                >
                    Iniciar sesión
                </a>
                <a
                    v-if="$page.props.user == null"
                    href="/register"
                    class="px-5 py-3 ml-2 text-base font-bold bg-white shadow-sm rounded-xl public-navigation-action text-enriqueto_green hover:shadow-md"
                >
                    Regístrate
                </a>

                <div class="flex items-center pl-3 ml-3 border-l border-enriqueto_green border-opacity-20">
                    <CurrencySwitcher />
                    <DarkModeSwitcher />
                    <div v-if="$page.props.user != null" class="ml-3">
                        <NotificationComponent />
                    </div>
                </div>
            </div>

            <button
                type="button"
                class="inline-flex items-center justify-center flex-shrink-0 w-11 h-11 bg-white border rounded-xl shadow-sm lg:hidden border-enriqueto_green border-opacity-20 text-enriqueto_green"
                :aria-expanded="openMenu"
                aria-controls="mobile-navigation"
                :aria-label="openMenu ? 'Cerrar menú' : 'Abrir menú'"
                @click="toggleMenu"
            >
                <span class="text-3xl material-symbols-outlined">{{ openMenu ? 'close' : 'menu' }}</span>
            </button>
        </div>
    </nav>

    <transition name="mobile-drawer">
        <div v-if="openMenu" id="mobile-navigation" class="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Navegación principal">
            <button type="button" class="absolute inset-0 w-full h-full bg-black bg-opacity-40" aria-label="Cerrar menú" @click="closeMenu"></button>

            <aside class="absolute top-0 right-0 flex flex-col w-11/12 h-full max-w-sm overflow-hidden bg-light_yellow mobile-navigation-panel">
                <header class="flex items-center justify-between flex-shrink-0 px-5 py-4 border-b border-light_gray bg-enriqueto_yellow">
                    <Link href="/" aria-label="Ir al inicio" @click="closeMenu">
                        <img
                            class="object-contain w-32"
                            :src="darkMode ? '/images/enriquetofrutas_darklogo.png' : '/images/enriquetofrutas_logo.png'"
                            alt="Enriqueto"
                        />
                    </Link>
                    <button type="button" class="inline-flex items-center justify-center w-11 h-11 bg-white border rounded-xl border-enriqueto_green border-opacity-20 text-enriqueto_green" aria-label="Cerrar menú" @click="closeMenu">
                        <span class="text-3xl material-symbols-outlined">close</span>
                    </button>
                </header>

                <div class="flex-1 px-5 py-6 overflow-y-auto">
                    <SearchComponent />

                    <Link
                        v-if="$page.props.user != null"
                        href="/profile"
                        class="flex items-center gap-3 p-4 mt-5 bg-white border shadow-sm rounded-2xl border-light_gray text-enriqueto_green"
                        @click="closeMenu"
                    >
                        <span class="material-symbols-outlined">account_circle</span>
                        <span class="min-w-0">
                            <small class="block text-xs font-semibold text-gray">Cuenta</small>
                            <strong class="block overflow-hidden text-base truncate">{{ $page.props.user.name }}</strong>
                        </span>
                        <div class="ml-auto" @click.stop>
                            <NotificationComponent />
                        </div>
                    </Link>

                    <div class="mt-6">
                        <p class="px-1 mb-2 text-xs font-bold tracking-widest uppercase text-gray">Explorar</p>
                        <Link href="/" class="mobile-navigation-link" @click="closeMenu">
                            <span class="material-symbols-outlined">storefront</span><span>Mercado</span>
                        </Link>
                        <Link href="/taxonomia" class="mobile-navigation-link" @click="closeMenu">
                            <span class="material-symbols-outlined">account_tree</span><span>Taxonomía</span>
                        </Link>
                        <Link href="/portfolio" class="mobile-navigation-link" @click="closeMenu">
                            <span class="material-symbols-outlined">agriculture</span><span>Portafolio</span>
                        </Link>
                        <Link v-if="$page.props.isAdmin === true" href="/admin" class="mobile-navigation-link" @click="closeMenu">
                            <span class="material-symbols-outlined">dashboard</span><span>Administración</span>
                        </Link>
                        <a v-if="$page.props.isAdmin === true" href="/api/documentation" class="mobile-navigation-link" @click="closeMenu">
                            <span class="material-symbols-outlined">api</span><span>API</span>
                        </a>
                    </div>

                    <div class="grid grid-cols-2 gap-3 mt-6">
                        <div class="p-4 bg-white border rounded-2xl border-light_gray">
                            <p class="mb-2 text-xs font-bold tracking-wider uppercase text-gray">Moneda</p>
                            <CurrencySwitcher />
                        </div>
                        <div class="flex items-center justify-between p-4 bg-white border rounded-2xl border-light_gray">
                            <div>
                                <p class="text-xs font-bold tracking-wider uppercase text-gray">Apariencia</p>
                                <p class="mt-1 text-sm font-bold text-enriqueto_green">{{ darkMode ? 'Claro' : 'Oscuro' }}</p>
                            </div>
                            <DarkModeSwitcher />
                        </div>
                    </div>
                </div>

                <footer class="flex-shrink-0 p-5 border-t border-light_gray bg-white">
                    <div v-if="$page.props.user == null" class="grid gap-3">
                        <a href="/login" class="w-full px-5 py-3 text-base font-bold text-center border rounded-xl border-enriqueto_green text-enriqueto_green" @click="closeMenu">Iniciar sesión</a>
                        <a href="/register" class="w-full px-5 py-3 text-base font-bold text-center text-white rounded-xl bg-enriqueto_green" @click="closeMenu">Crear una cuenta</a>
                    </div>
                    <Link v-else href="/logout" method="post" as="button" type="button" class="w-full px-5 py-3 text-base font-bold text-center border rounded-xl border-enriqueto_green text-enriqueto_green" @click="closeMenu">Cerrar sesión</Link>
                </footer>
            </aside>
        </div>
    </transition>
</template>

<style scoped>
.public-navigation {
    box-shadow: 0 2px 12px rgba(3, 102, 46, 0.08);
}

.public-navigation-logo {
    width: 138px;
    height: 44px;
}

.public-navigation-links {
    transform: translateY(4px);
}

.public-navigation-link {
    border-radius: 0.6rem;
    color: #03662E;
    font-size: 1rem !important;
    font-weight: 700 !important;
    letter-spacing: 0.01em;
    line-height: 1.25rem;
    padding: 0.7rem 0.8rem;
    transition: background-color 150ms ease, transform 150ms ease;
}

.public-navigation-action {
    font-size: 1rem !important;
    font-weight: 700 !important;
}

.public-navigation-link:hover {
    background: rgba(255, 255, 255, 0.55);
    transform: translateY(-1px);
}

.mobile-navigation-link {
    align-items: center;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 0.9rem;
    color: #03662E;
    display: flex;
    font-size: 1.05rem;
    font-weight: 700 !important;
    gap: 0.85rem;
    margin-top: 0.65rem;
    padding: 0.9rem 1rem;
}

.mobile-navigation-link span:not(.material-symbols-outlined) {
    font-weight: 700 !important;
}

.mobile-navigation-link .material-symbols-outlined {
    font-size: 1.45rem;
}

.mobile-drawer-enter-active,
.mobile-drawer-leave-active {
    transition: opacity 220ms ease;
}

.mobile-drawer-enter-active .mobile-navigation-panel,
.mobile-drawer-leave-active .mobile-navigation-panel {
    transition: transform 250ms ease;
}

.mobile-drawer-enter-from,
.mobile-drawer-leave-to {
    opacity: 0;
}

.mobile-drawer-enter-from .mobile-navigation-panel,
.mobile-drawer-leave-to .mobile-navigation-panel {
    transform: translateX(100%);
}

@media (max-width: 1023px) {
    .public-navigation-logo {
        width: 126px;
        height: 40px;
    }
}
</style>
